<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import flowDefinitions from 'dashboard/api/flowDefinitions';

const { t } = useI18n();
const router = useRouter();

const records = ref([]);
const loading = ref(true);
const deletingId = ref(null);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await flowDefinitions.get();
    records.value = data.payload || [];
  } finally {
    loading.value = false;
  }
};

onMounted(load);

const hasRows = computed(() => records.value.length > 0);

const openNew = () => {
  router.push({ name: 'settings_flows_edit', params: { flowId: 'new' } });
};

const openEdit = id => {
  router.push({ name: 'settings_flows_edit', params: { flowId: String(id) } });
};

const remove = async id => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('FLOWS.DELETE_CONFIRM'))) return;
  deletingId.value = id;
  try {
    await flowDefinitions.delete(id);
    useAlert(t('FLOWS.DELETE_SUCCESS'));
    await load();
  } catch (e) {
    useAlert(t('FLOWS.DELETE_ERROR'));
  } finally {
    deletingId.value = null;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('FLOWS.LOADING')"
    :no-records-found="!hasRows"
    :no-records-message="$t('FLOWS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('FLOWS.HEADER')"
        :description="$t('FLOWS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-plus"
            :label="$t('FLOWS.NEW')"
            size="sm"
            @click="openNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="mx-8 max-w-5xl pb-8">
        <div
          v-for="row in records"
          :key="row.id"
          class="flex items-center justify-between border-b border-n-weak py-4"
        >
          <div>
            <div class="text-base font-medium text-n-slate-12">
              {{ row.name }}
            </div>
            <div class="text-sm text-n-slate-10">
              {{
                $t('FLOWS.LIST_META', {
                  status: row.active
                    ? $t('FLOWS.ACTIVE')
                    : $t('FLOWS.INACTIVE'),
                  id: row.id,
                })
              }}
            </div>
          </div>
          <div class="flex gap-2">
            <Button
              variant="faded"
              size="sm"
              :label="$t('FLOWS.EDIT')"
              @click="openEdit(row.id)"
            />
            <Button
              variant="faded"
              color="ruby"
              size="sm"
              :label="$t('FLOWS.DELETE')"
              :is-loading="deletingId === row.id"
              @click="remove(row.id)"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
